import React from 'react';
import { MarkdownRenderer } from "../MarkdownRenderer";
import { DefaultToolCallRenderer } from '../ToolRenderers';
import { DataTable } from "../../ui/datatable/datatable";
import { Button } from "../../ui/button";
import { ExternalLink } from 'lucide-react';
import { ColumnDef } from "@tanstack/react-table";

// Message interface for the ListIssuesToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// GitHub Issue response structure
interface GitHubIssue {
    html_url: string;
    number: number;
    title: string;
    state: string;
    created_at: string;
    user: {
        login: string;
        avatar_url: string;
        html_url: string;
    };
    labels: Array<{
        name: string;
        color: string;
    }>;
    assignees: Array<{
        login: string;
        avatar_url: string;
        html_url: string;
    }>;
}

// DataTable row type
interface IssueRow {
    number: number;
    title: string;
    state: string;
    created_at: string;
    author: string;
    labels: string;
    assignees: string;
    html_url: string;
}

// Tool renderer for listing GitHub issues
export function ListIssuesToolRenderer({ message, isToolCall }: ToolRendererProps) {
    // If this is just the tool call and not the response, render the default tool call renderer
    if (!message.tool_call_response_content) {
        return <DefaultToolCallRenderer message={message} />;
    }

    let issues: GitHubIssue[] | null = null;
    try {
        issues = JSON.parse(message.tool_call_response_content);
    } catch (error) {
        console.error('Error parsing GitHub issues response data:', error);
        return null;
    }

    // Validate response
    if (!issues || !Array.isArray(issues)) {
        return null;
    }

    // Format the data for the DataTable
    const data: IssueRow[] = issues.map(issue => ({
        number: issue.number,
        title: issue.title,
        state: issue.state,
        created_at: new Date(issue.created_at).toLocaleString(),
        author: issue.user.login,
        labels: issue.labels.map(label => label.name).join(', '),
        assignees: issue.assignees.map(assignee => assignee.login).join(', '),
        html_url: issue.html_url
    }));

    const columns: ColumnDef<IssueRow>[] = [
        {
            id: "number",
            accessorKey: "number",
            header: "#",
            cell: ({ row }) => <span className="text-textmedium">#{row.original.number}</span>
        },
        {
            id: "title",
            accessorKey: "title",
            header: "Title",
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <span className="text-textlight">{row.original.title}</span>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-textmedium hover:text-textlight"
                        onClick={() => window.open(row.original.html_url, '_blank')}
                    >
                        <ExternalLink className="h-4 w-4" />
                    </Button>
                </div>
            )
        },
        {
            id: "state",
            accessorKey: "state",
            header: "State",
            cell: ({ row }) => (
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                    row.original.state === 'open' 
                        ? 'bg-green-500/10 text-green-400' 
                        : 'bg-red-500/10 text-red-400'
                }`}>
                    {row.original.state}
                </span>
            )
        },
        {
            id: "created_at",
            accessorKey: "created_at",
            header: "Created",
            cell: ({ row }) => <span className="text-textmedium">{row.original.created_at}</span>
        },
        {
            id: "author",
            accessorKey: "author",
            header: "Author",
            cell: ({ row }) => <span className="text-textmedium">{row.original.author}</span>
        },
        {
            id: "labels",
            accessorKey: "labels",
            header: "Labels",
            cell: ({ row }) => <span className="text-textmedium">{row.original.labels || '-'}</span>
        },
        {
            id: "assignees",
            accessorKey: "assignees",
            header: "Assignees",
            cell: ({ row }) => <span className="text-textmedium">{row.original.assignees || '-'}</span>
        }
    ];

    // No filters for this table
    const filters: { filteringColumn: string; title: string; }[] = [];

    return (
        <div className="w-full space-y-4">
            <MarkdownRenderer content={message.content} />
            
            <div className="border rounded overflow-hidden bg-backgroundmedium">
                <DataTable 
                    columns={columns} 
                    data={data}
                    tableTitle="GitHub Issues"
                    showPagination={true}
                    removeBorder={true}
                    textFilteringColumnName="title"
                    textFilteringPlaceholder="Search issues..."
                    columnConfigs={[
                        { id: "number", isFilterable: false },
                        { id: "title", isFilterable: true },
                        { id: "state", isFilterable: true },
                        { id: "created_at", isFilterable: false },
                        { id: "author", isFilterable: true },
                        { id: "labels", isFilterable: true },
                        { id: "assignees", isFilterable: true }
                    ]}
                    filters={filters}
                />
            </div>
        </div>
    );
}

// Static method to determine if this renderer should render a message
ListIssuesToolRenderer.shouldRender = (message: Message): boolean => {
    return message.tool_name === "list_issues";
}; 